import React, { forwardRef, ReactElement, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { useId } from "@reach/auto-id";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import produce, { Draft } from 'immer';
import { usePopper } from 'react-popper';
import { CSSTransition } from 'react-transition-group';
import { useFilteredList, useLoadMore } from 'hooks';
import { AsyncStatus, Keys } from 'enums';
import useForkRef from 'hooks/useForkRef';
import { IMultiSelectChipProps, MultiSelectChip } from './MultiSelectChip';
import { IMultiSelectOptionProps, MultiSelectOption } from './MultiSelectOption';
import './MultiSelect.scss';
import { clamp } from 'services/Utils';
import scrollIntoView from 'scroll-into-view-if-needed';

// Redeclare forwardRef to support generics:
// https://fettblog.eu/typescript-react-generic-forward-refs/#option-3%3A-augment-forwardref
declare module "react" {
    function forwardRef<T, P = {}>(
        render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
    ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

// Popper modifier to make the dropdown the same width as the input.
// From: https://popper.js.org/docs/v2/modifiers/community-modifiers/
const sameWidth = {
    name: "sameWidth",
    enabled: true,
    phase: "beforeWrite",
    requires: ["computeStyles"],
    fn: ({ state }) => {
        state.styles.popper.width = `${state.rects.reference.width}px`;
    },
    effect: ({ state }) => {
        state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
    }
};

const animationDuration = 50;

interface IMultiSelectState {
    inputText: string,
    isOpen?: boolean,
    /**
     * We support a nearly unlimited number of options, but we only render the first few.
     * When the user scrolls down, we'll increase this prop to render more options.
     */
     maxOptionsToRender: number,
    /**
     * Controls which option is highlighted. Gets set via mouse hover or keyboard navigation.
     * We're using "index" instead of "id" because "id" may not be unique.
     */
    navigationOptionIndex: number,
}

export type IOptionId = string | number;

export interface IMultiSelectProps<T> {
    className?: string,
    components?: {
        chip?: (props: IMultiSelectChipProps<T>) => ReactElement,
        option?: (props: IMultiSelectOptionProps<T>) => ReactElement,
    }
    'data-testid'?: string,
    id?: string,
    options: T[],
    ref?: React.Ref<HTMLInputElement>,
    value: T[],
    getOptionId: (option: T) => IOptionId,
    getOptionSearchValue: (option: T) => string,
    onChange: (newValue: T[]) => void,
    onPaste?: (event) => void,
}

function _MultiSelect<T>({
    className,
    components = {},
    "data-testid": dataTestId,
    id: htmlID,
    options = [],
    value = [],
    getOptionId,
    getOptionSearchValue,
    onChange,
    onPaste,
}: IMultiSelectProps<T>, forwardedRef?: React.Ref<HTMLInputElement>) {
    // Allow consumers to use their own components if they want to customize.
    if (!components.chip) components.chip = MultiSelectChip;
    if (!components.option) components.option = MultiSelectOption;

    const renderChunkSize = 100;
    const [{
        inputText,
        isOpen,
        maxOptionsToRender,
        navigationOptionIndex,
    }, setState] = useState<IMultiSelectState>({
        inputText: '',
        maxOptionsToRender: renderChunkSize,
        navigationOptionIndex: -1,
    });

    const filteredOptions = useFilteredList(options, inputText, getOptionSearchValue);
    const displayedOptions = useMemo(() => {
        return filteredOptions.slice(0, maxOptionsToRender);
    }, [filteredOptions, maxOptionsToRender]);

    const selectedOptionIDs = useMemo(() => {
        const selected: {[optionId: IOptionId]: boolean} = {};

        for (const option of value) {
            selected[getOptionId(option)] = true;
        }

        return selected;
    }, [options, value]);

    // Setup popper. It needs these to be "useState" instead of "useRef" so that it rerenders when they change.
    const [multiselectEl, setMultiselectEl] = useState<HTMLDivElement>(null);
    const [dropdownContainerEl, setDropdownContainerEl] = useState<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>();

    const pop = usePopper(multiselectEl, dropdownContainerEl, {
        placement: 'bottom-start',
        modifiers: [
            { name: 'flip', enabled: false },
            sameWidth as any,
        ],
    });

    // ----------------------- Effects ------------------------

    // When our value or text changes, our input can change height, so we need to tell Popper to update.
    // There is a slight performance hit to doing this because it's an additional render.
    const isPopperFirstChange = useRef(true);
    useEffect(() => {
        // Ignore first render
        if (isPopperFirstChange.current) {
            isPopperFirstChange.current = false;
            return;
        }

        // Defer to avoid flushSync issues
        const timeout = setTimeout(() => {
            pop.forceUpdate?.();
        }, 0);

        return () => clearTimeout(timeout);
    }, [value, inputText])

    // InfiniteScroll: This makes the dropdown render more options if the user scrolls down.
    const { scrollerRef } = useLoadMore(() => {
        if (maxOptionsToRender < filteredOptions.length) {
            setState(produce(draft => {
                draft.maxOptionsToRender += renderChunkSize;
            }))
        }
    });

    function resetInfiniteScroll() {
        setState(produce(draft => {
            draft.maxOptionsToRender = renderChunkSize;
        }))
    }

    // To scroll to an option for keyboard nav, we need a ref for every visible option.
    const displayedOptionRefs = useMemo(() => {
        return displayedOptions.map(option => React.createRef<HTMLDivElement>());
    }, [displayedOptions])

    // For accessibility, we need an id's for each item, and for some other elements
    const dropdownWrapHtmlId = useId();
    const dropdownHtmlId = useId();
    const displayedOptionHtmlIds = useMemo(() => {
        return displayedOptions.map((option, i) => {
            return `${dropdownWrapHtmlId}-option-${i}`
        })
    }, [dropdownWrapHtmlId, displayedOptions]);

    let navigationOptionHtmlId = undefined;
    if (navigationOptionIndex !== -1 && navigationOptionIndex < displayedOptionHtmlIds.length) {
        navigationOptionHtmlId = displayedOptionHtmlIds[navigationOptionIndex];
    }

    // Close on mousedown outside
    useEffect(() => {
        if (isOpen) {
            const onClickWindow = (e) => {
                // Ignore clicks from inside our elements
                if (!dropdownContainerEl || dropdownContainerEl.contains(e.target)) return;
                if (!multiselectEl || multiselectEl.contains(e.target)) return;

                close(false);
            }

            // We're using capture to work around an issue in React where "contains" returns false
            // if the element is removed in the next render, which happens for the "remove all" button.
            // https://github.com/facebook/react/issues/20325
            window.addEventListener('mousedown', onClickWindow, { capture: true });
            return () => window.removeEventListener('mousedown', onClickWindow, { capture: true });
        }
    }, [isOpen, dropdownContainerEl, multiselectEl])

    // ---------------- Handlers and Callbacks ----------------

    function focusInput() {
        inputRef.current?.focus();
    }

    function onToggleOpen() {
        isOpen ? close() : open();
    }

    function open(moveFocus = true) {
        if (moveFocus) {
            focusInput();
        }
        setState(produce(draft => {
            draft.isOpen = true;
        }));
        // Try and move navigation to the first option.
        trySetNavigation(0, true);
    }

    function close(moveFocus = true) {
        if (moveFocus) {
            focusInput();
        }
        // We don't want to clear our state until after we finish closing,
        // or else it causes a flash.
        setState(produce(draft => {
            draft.isOpen = false;
        }));
    }

    // Clear our state after the dropdown closes
    const cssTransitionOnExited = useCallback(() => {
        setState(produce(draft => {
            draft.inputText = '';
            draft.navigationOptionIndex = -1;
        }));
    }, []);

    function onClickChipsContainer(e: React.MouseEvent<HTMLDivElement>) {
        // Ignore clicks on chip close buttons.
        if (e.isDefaultPrevented()) return;
        
        // If the input was clicked, then open if we're not already. Otherwise, do nothing.
        // The user may click in the input to adjust their text.
        if (inputRef.current && e.target === inputRef.current) {
            if (!isOpen) {
                open();
            }
        } else {
            // The user clicked the multiselect or a tag, in which case we'll toggle opening.
            onToggleOpen();
        }
    }

    function selectOption(option: T) {
        inputRef.current?.focus();

        setState(produce(draft => {
            draft.inputText = '';
        }));

        // Append all options with the same id.
        const id = getOptionId(option);
        const optionsToAdd = options.filter(option => getOptionId(option) === id);
        const newValue = [...value, ...optionsToAdd];
        onChange(newValue);
    }

    function unselectOption(option: T) {
        inputRef.current?.focus();

        setState(produce(draft => {
            draft.inputText = '';
        }));

        // Remove all options with this id.
        const id = getOptionId(option);
        const newValue = value.filter(option => getOptionId(option) !== id);
        onChange(newValue);
    }

    function onChangeInputText(e: React.ChangeEvent<HTMLInputElement>) {
        e.preventDefault();
        resetInfiniteScroll();

        const val = e.target.value;
        setState(produce(draft => {
            draft.inputText = val;
        }));

        if (!isOpen) {
            open();
        }

        // Select first item
        trySetNavigation(0, true);
    }

    function onRemoveAll(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        focusInput();

        setState(produce(draft => {
            draft.inputText = '';
            draft.navigationOptionIndex = -1;
        }));

        onChange([]);
    }

    // ---------------------- Navigation ----------------------

    function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.isDefaultPrevented()) return;
        
        const preventDefaults = () => {
            e.preventDefault();
            e.stopPropagation();
        }

        switch (e.key) {
            case Keys.ArrowDown:
            case Keys.ArrowUp:
                preventDefaults();
                if (isOpen) {
                    tryMoveNavigation(e.key === Keys.ArrowDown ? 1 : -1);
                } else {
                    open();
                }
                break;
            case Keys.Enter:
                preventDefaults();
                if (!isOpen) {
                    open();
                } else {
                    tryToggleNavigationOption();
                }
                break;
            case ' ':
                // If we don't have any input text, then the user probably meant to open the dropdown or select an item.
                // This is more likely than that they wanted to type a leading space
                if (!inputText) {
                    preventDefaults();
                    if (!isOpen) {
                        open();
                    } else {
                        tryToggleNavigationOption();
                    }
                }
                break;
            case Keys.Escape: 
                preventDefaults();
                if (isOpen) {
                    close(false);
                }
                break;
            case Keys.Tab:
                if (isOpen) {
                    // Not preventing default so that focus moves on to next element.
                    close(false);
                }
                break;
            case Keys.Backspace:
                // If we don't have any text, then remove the last selected option.
                if (!inputText && value.length > 0) {
                    onChange(value.slice(0, value.length - 1));
                }
                break;
        }
    }

    // If we already have a navigation item index, add "diff" to it. 
    // Otherwise, select the first item.
    function tryMoveNavigation(diff: number) {
        let index = 0;
        if (navigationOptionIndex !== -1) {
            // Limit it to the bounds of the array.
            index = clamp(navigationOptionIndex + diff, 0, filteredOptions.length - 1);
        }
        trySetNavigation(index, true);
    }

    // Makes sure the new index is valid before setting it.
    // If it's out of bounds, we'll keep it within the valid range.
    // This is a separate function because it's cleaner than having to do these safety checks all over the place.
    function trySetNavigation(index: number, scrollToOption: boolean) {
        if (index !== -1 && filteredOptions.length > 0) {
            index = clamp(index, 0, filteredOptions.length - 1);
        }

        setState(produce(draft => {
            draft.navigationOptionIndex = index;
        }))

        // Scroll to the selected option.
        if (scrollToOption && index !== -1) {
            const el = displayedOptionRefs?.[index]?.current;
            if (el) {
                scrollIntoView(el, {
                    scrollMode: 'if-needed',
                    // If we're scrolling down, we need to snap to the vertical end of the item.
                    // Else, snap to the vertical start.
                    block: (index > navigationOptionIndex) ? 'end' : 'start',
                })
            }
        }
    }

    // Selects the navigated option, if there is one.
    function tryToggleNavigationOption() {
        const option = filteredOptions?.[navigationOptionIndex];
        // We're doing an explicit undefined check in case the option is an empty string.
        if (option !== undefined) {
            const id = getOptionId(option);
            if (selectedOptionIDs[id]) {
                unselectOption(option);
            } else {
                selectOption(option);
            }
        }
    }

    return <>
        <div
            className={cn('multiSelect', className, {
                open: isOpen,
            })}
            data-testid={dataTestId}
            id={htmlID}
            ref={setMultiselectEl}
            onClick={(e) => undefined}
        >
            <div
                className="multiSelect_chipsWrap"
                onClick={onClickChipsContainer}
            >
                {value.map((option, i) => (
                    <components.chip
                        index={i}
                        key={i}
                        onRemove={() => unselectOption(option)}
                        option={option}
                    />
                ))}
                <div className="multiSelect_inputWrap">
                    {/*
                        We're keeping focus on this input and using "activedescendant" to tell
                        screenreaders which option is navigated to. Each option also sets "aria-selected"
                        to communicate what is currently selected.
                    */}
                    <input
                        onPaste={onPaste}
                        aria-activedescendant={navigationOptionHtmlId}
                        aria-autocomplete="list"
                        aria-expanded={isOpen}
                        aria-haspopup="listbox"
                        aria-controls={dropdownWrapHtmlId}
                        aria-owns={dropdownWrapHtmlId}
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        className="multiSelect_input"
                        onChange={onChangeInputText}
                        onKeyDown={onKeyDown}
                        ref={useForkRef(inputRef, forwardedRef)}
                        role="combobox"
                        spellCheck="false"
                        type="text"
                        value={inputText}
                    />
                    <div className="multiSelect_inputSpaceFill" aria-hidden={true}>
                        {inputText}
                    </div>
                </div>
            </div>
            {value.length > 0 && (<>
                <button
                    className="multiSelect_removeAllButton"
                    type="button"
                    onClick={onRemoveAll}
                    // We're trying to keep things as simple as possible for screenreaders,
                    // so we're not including this button so that we have 1 tab stop per control.
                    // Options can still be removed by pressing "backspace".
                    aria-hidden={true}
                    tabIndex={-1}
                >
                    <FontAwesomeIcon icon="times" />
                </button>
                <div className="multiSelect_buttonSeparator" />
            </>)}
            <button
                className="multiSelect_openButton"
                type="button"
                onClick={onToggleOpen}
                // We're allowing opening via Enter or Down Arrow, so it's unnecessary to include this button in tab navigation.
                // We only want 1 tab stop for the whole component.
                aria-hidden={true}
                tabIndex={-1}
            >
                <FontAwesomeIcon icon="chevron-down" />
            </button>
        </div>

        <div
            className={cn('multiSelect_dropdownWrap', {
                open: isOpen,
            })}
            id={dropdownWrapHtmlId}
            ref={setDropdownContainerEl}
            role="listbox"
            style={pop.styles.popper}
            {...pop.attributes.popper}
        >
            <CSSTransition 
                classNames="multiSelect_dropdown"
                in={isOpen}
                onExited={cssTransitionOnExited}
                timeout={animationDuration}
                unmountOnExit
            >
                <div
                    className="multiSelect_dropdown"
                    id={dropdownHtmlId}
                    ref={scrollerRef}
                >
                    {displayedOptions.length === 0 && (
                        <div className="multiSelect_noResults">
                            No items found.
                        </div>
                    )}
                    {displayedOptions.length > 0 && displayedOptions.map((option, i) => (
                        <components.option
                            id={displayedOptionHtmlIds[i]}
                            index={i}
                            isFocused={navigationOptionIndex === i}
                            isSelected={!!selectedOptionIDs[getOptionId(option)]}
                            key={i}
                            option={option}
                            onMouseEnter={() => trySetNavigation(i, false)}
                            onSelect={() => selectOption(option)}
                            onUnselect={() => unselectOption(option)}
                            ref={displayedOptionRefs[i]}
                            role="listitem"
                        />
                    ))}
                </div>
            </CSSTransition>
        </div>
    </>
};

export const MultiSelect = forwardRef(_MultiSelect);